import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import type { CellObject, WorkSheet } from 'xlsx';

import type {
  TableExportColumn,
  TableExportSheet,
  TableExportValue,
} from './table-export.model';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** Excel's built-in short date (id 14) and date-time (id 22): rendered in the reader's locale. */
const XLSX_DATE_FORMAT = 'm/d/yy';
const XLSX_DATE_TIME_FORMAT = 'm/d/yy h:mm';
const SHEET_NAME_MAX = 31;
const COLUMN_WIDTH_MAX = 60;
/** Day zero of Excel's serial dates. */
const EXCEL_EPOCH = DateTime.utc(1899, 12, 30);

/**
 * Excel serial of the value's wall clock. Serials carry no zone, so the digits the
 * account sees are re-read as UTC before counting days from the epoch.
 */
function toExcelSerial(value: DateTime): number {
  const wallClock = value.setZone('utc', { keepLocalTime: true });
  return Math.round(wallClock.diff(EXCEL_EPOCH, 'seconds').seconds) / 86400;
}

function toCell(
  value: TableExportValue,
  column: TableExportColumn
): CellObject | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return { t: 's', v: value };
  }
  if (typeof value === 'number') {
    return column.numberFormat
      ? { t: 'n', v: value, z: column.numberFormat }
      : { t: 'n', v: value };
  }
  return {
    t: 'n',
    v: toExcelSerial(value),
    z: column.type === 'date' ? XLSX_DATE_FORMAT : XLSX_DATE_TIME_FORMAT,
  };
}

function textLength(value: TableExportValue, column: TableExportColumn): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).length;
  }
  return column.type === 'date' ? 10 : 16;
}

function sheetNames(sheets: TableExportSheet[]): string[] {
  const used = new Set<string>();
  return sheets.map((sheet, index) => {
    const base =
      sheet.name.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, SHEET_NAME_MAX) ||
      `${index + 1}`;
    let name = base;
    let copy = 2;
    while (used.has(name.toLowerCase())) {
      const suffix = ` (${copy})`;
      name = base.slice(0, SHEET_NAME_MAX - suffix.length) + suffix;
      copy++;
    }
    used.add(name.toLowerCase());
    return name;
  });
}

/**
 * XLSX workbook, one tab per sheet in the given order (e.g. "Resumo" then the
 * data). Header row first; numbers and `DateTime`s are numeric cells — dates
 * with Excel's locale-aware date format — so the reader can sum and sort them.
 * A sheet with `autoFilterRows` gets filter arrows on its header.
 *
 * `xlsx` is loaded on demand: it stays out of every bundle that imports
 * `@shared/utils` without exporting anything.
 */
export async function buildTableXlsx(
  sheets: TableExportSheet[]
): Promise<ArrayBuffer> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.utils.book_new();
  const names = sheetNames(sheets);
  sheets.forEach((sheet, index) => {
    const worksheet: WorkSheet = {};
    sheet.columns.forEach((column, col) => {
      worksheet[XLSX.utils.encode_cell({ r: 0, c: col })] = {
        t: 's',
        v: column.header,
      };
    });
    sheet.rows.forEach((row, rowIndex) => {
      sheet.columns.forEach((column, col) => {
        const cell = toCell(row[col], column);
        if (cell) {
          worksheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: col })] = cell;
        }
      });
    });
    worksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: {
        r: sheet.rows.length,
        c: Math.max(sheet.columns.length - 1, 0),
      },
    });
    if (sheet.autoFilterRows !== undefined) {
      worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: {
            r: Math.min(sheet.autoFilterRows, sheet.rows.length),
            c: Math.max(sheet.columns.length - 1, 0),
          },
        }),
      };
    }
    worksheet['!cols'] = sheet.columns.map((column, col) => ({
      wch: Math.min(
        Math.max(
          column.header.length,
          ...sheet.rows.map((row) => textLength(row[col], column))
        ) + 2,
        COLUMN_WIDTH_MAX
      ),
    }));
    XLSX.utils.book_append_sheet(workbook, worksheet, names[index]);
  });
  return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
}

/** Downloads `buildTableXlsx` as `<fileBaseName>.xlsx`. */
export async function exportTableXlsx(
  fileBaseName: string,
  sheets: TableExportSheet[]
): Promise<void> {
  const buffer = await buildTableXlsx(sheets);
  saveAs(new Blob([buffer], { type: XLSX_MIME }), `${fileBaseName}.xlsx`);
}
